import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-black.svg";
import { ReactComponent as MinusSvg } from "@/src/assets/icons/icon-minus.svg";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { IconButton } from "../../atom/Button";
import Checkbox from "../../atom/Checkbox";

interface CheckboxCountFieldProps {
  key?: string;
  disabled?: boolean;
  checkboxDisabled?: boolean;
  isChecked?: boolean;
  isRequired?: boolean;
  forceChecked?: boolean;
  count?: number;
  min?: number;
  max?: number;
  bottomAccessory?: ReactNode;
  label?: ReactNode;
  className?: string;
  onCheckChange?: (key: string, checkState: boolean) => void;
  onCountUp?: (count: number) => void;
  onCountDown?: (count: number) => void;
}

function CheckboxCountField({
  key,
  disabled = false,
  checkboxDisabled = false,
  isRequired = false,
  forceChecked = false,
  isChecked,
  count = 0,
  min = 1,
  max = 3,
  bottomAccessory,
  label,
  className,
  onCheckChange,
  onCountDown,
  onCountUp,
}: CheckboxCountFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const isCountOverMaxCount = count >= max;
  const isCountUnderMinCount = count <= min;

  // forceChecked일 때는 항상 체크된 상태로 취급
  const effectiveIsChecked = forceChecked ? true : isChecked;

  const handleLabelClick = () => {
    if (!disabled && !checkboxDisabled && !forceChecked) {
      onCheckChange?.(key || id, !isChecked);
    }
  };

  return (
    <FieldArticle
      data-checked={!!effectiveIsChecked}
      data-disabled={disabled}
      className={className}
    >
      <FieldContent>
        <FieldLabelContent data-disabled={disabled || !effectiveIsChecked}>
          <Checkbox
            checked={forceChecked ? true : !!isChecked}
            disabled={disabled || checkboxDisabled || forceChecked}
            data-disabled={disabled || checkboxDisabled || forceChecked}
            onChange={(e) => {
              if (!forceChecked && !checkboxDisabled) {
                onCheckChange?.(key || id, e.target.checked);
              }
            }}
          />
          <span className="field-label" onClick={handleLabelClick}>
            {label || t("common:empty")}
          </span>
        </FieldLabelContent>
        <FieldCountContainer>
          <StyledIconButton
            buttonColor="black"
            buttonGrade="tertiary"
            buttonSize={32}
            onClick={() => onCountDown?.(count)}
            disabled={
              ((disabled || !effectiveIsChecked) && !isRequired && !forceChecked) ||
              isCountUnderMinCount
            }
          >
            <MinusIcon
              data-disabled={
                ((disabled || !effectiveIsChecked) && !isRequired && !forceChecked) ||
                isCountUnderMinCount
              }
            />
          </StyledIconButton>

          <CountContainer
            data-disabled={(disabled || !effectiveIsChecked) && !isRequired && !forceChecked}
          >
            {count}
          </CountContainer>

          <StyledIconButton
            buttonColor="black"
            buttonGrade="tertiary"
            buttonSize={32}
            onClick={() => onCountUp?.(count)}
            disabled={
              ((disabled || !effectiveIsChecked) && !isRequired && !forceChecked) || isCountOverMaxCount
            }
          >
            <AddIcon
              data-disabled={
                ((disabled || !effectiveIsChecked) && !isRequired && !forceChecked) || isCountOverMaxCount
              }
            />
          </StyledIconButton>
        </FieldCountContainer>
      </FieldContent>
      {bottomAccessory && (
        <BottomAccessoryContainer>{bottomAccessory}</BottomAccessoryContainer>
      )}
    </FieldArticle>
  );
}

export default CheckboxCountField;

const FieldArticle = styled.article`
  width: 100%;
  height: 100%;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;

  &[data-checked="true"] {
    background: ${colorSet.blue10};
  }
  &[data-checked="false"] {
    background: ${colorSet.gray11};
  }
`;

const FieldContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldLabelContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${typo.b7r};
  color: ${colorSet.gray2};
  flex: 1;
  min-width: 0;

  span.field-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    padding-right: 8px;
    cursor: pointer;
  }

  &[data-disabled="true"] {
    color: ${colorSet.gray7};
  }
`;

const FieldCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountContainer = styled.div`
  padding: 8px 0px;
  width: 50px;
  text-align: center;
  background: ${colorSet.white};
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.05);
  ${typo.b9r};
  color: ${colorSet.gray2};
  transition: all 0.15s;

  &[data-disabled="true"] {
    color: ${colorSet.gray7};
    border: 1px solid ${colorSet.gray8};
    background: ${colorSet.gray10};
  }
`;

const StyledIconButton = styled(IconButton)`
  &:disabled {
    background: ${colorSet.gray10};
    box-shadow:
      0px 1px 2px 0px rgba(5, 29, 57, 0.1),
      0px 0px 0px 0.5px rgba(160, 164, 171, 0.2);
  }
`;

const BottomAccessoryContainer = styled.div`
  width: 100%;
`;

const MinusIcon = styled(MinusSvg)`
  width: 20px;
  height: 20px;

  &[data-disabled="true"] {
    rect {
      fill: ${colorSet.gray8};
    }
  }
`;

const AddIcon = styled(AddSvg)`
  width: 20px;
  height: 20px;

  &[data-disabled="true"] {
    path {
      fill: ${colorSet.gray8};
    }
  }
`;
